/*
 * Two-pane department flyout.
 *
 * Opening and closing the panel itself is header.js's job — this file only
 * decides which of the seventeen panes is showing. It is a progressive
 * enhancement: with JavaScript off, the first pane stays visible and every
 * department name and subcategory link is still a working anchor.
 *
 * Three input methods, one state change:
 *   pointer   hovering a department swaps the pane after a short intent delay
 *   touch     the chevron button swaps the pane; the name still navigates
 *   keyboard  Up/Down walk the rail and swap as they go; Tab enters the pane
 *
 * The keyboard contract falls out of the DOM rather than being simulated:
 * inactive panes carry `hidden`, so their links are not focusable, and the rail
 * uses a roving tabindex so it is a single tab stop. Tab from the active
 * department therefore lands on the first link of the visible pane with no
 * interception at all.
 */
(function () {
  'use strict';

  /* Long enough that dragging the pointer down the rail does not strobe every
     pane in turn, short enough that a deliberate hover feels immediate. */
  var HOVER_DELAY = 180;

  var fine = window.matchMedia('(hover: hover) and (pointer: fine)');

  Array.prototype.forEach.call(document.querySelectorAll('[data-flyout]'), function (root) {
    var rail = root.querySelector('[data-flyout-rail]');
    var tabs = Array.prototype.slice.call(root.querySelectorAll('[data-flyout-tab]'));
    var chevs = Array.prototype.slice.call(root.querySelectorAll('[data-flyout-chev]'));
    var panes = Array.prototype.slice.call(root.querySelectorAll('[data-flyout-pane]'));
    if (!rail || !tabs.length || !panes.length) return;

    var timer = null;
    var target = function (el) {
      return el.getAttribute('data-flyout-target');
    };

    function activate(paneId, moveFocus) {
      panes.forEach(function (pane) {
        pane.hidden = pane.id !== paneId;
      });

      tabs.forEach(function (tab) {
        var on = target(tab) === paneId;
        /* Roving tabindex: exactly one department is in the tab order, so Tab
           from outside enters the rail once and Tab from inside leaves it. */
        tab.setAttribute('tabindex', on ? '0' : '-1');
        if (on) {
          tab.setAttribute('aria-current', 'true');
          if (moveFocus) tab.focus();
        } else {
          tab.removeAttribute('aria-current');
        }
      });

      chevs.forEach(function (chev) {
        chev.setAttribute('aria-expanded', String(target(chev) === paneId));
      });
    }

    /* ------------------------------------------------------------ pointer */

    tabs.forEach(function (tab) {
      var row = tab.closest('.mega-flyout__row') || tab;

      row.addEventListener('mouseenter', function () {
        if (!fine.matches) return;
        window.clearTimeout(timer);
        timer = window.setTimeout(function () {
          activate(target(tab), false);
        }, HOVER_DELAY);
      });

      /* Leaving a row cancels its pending swap, which is also what stops the
         pane changing when the pointer crosses the rail on its way into the
         pane it is already reading. */
      row.addEventListener('mouseleave', function () {
        window.clearTimeout(timer);
      });
    });

    root.addEventListener('mouseleave', function () {
      window.clearTimeout(timer);
    });

    /* -------------------------------------------------------------- touch */

    chevs.forEach(function (chev) {
      chev.addEventListener('click', function (e) {
        /* stopPropagation keeps header.js's outside-click handler from reading
           this as a click that should close the whole panel. */
        e.preventDefault();
        e.stopPropagation();
        window.clearTimeout(timer);
        activate(target(chev), false);
      });
    });

    /* ----------------------------------------------------------- keyboard */

    rail.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      var i = tabs.indexOf(document.activeElement);
      if (i === -1) return;

      var next = null;
      switch (e.key) {
        case 'ArrowDown':
          next = (i + 1) % tabs.length;
          break;
        case 'ArrowUp':
          next = (i - 1 + tabs.length) % tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = tabs.length - 1;
          break;
        case 'ArrowRight': {
          /* A shortcut into the pane. Tab does the same thing; this just
             matches what the rightward chevron implies. */
          var pane = document.getElementById(target(tabs[i]));
          var first = pane && pane.querySelector('a');
          if (first) {
            e.preventDefault();
            first.focus();
          }
          return;
        }
        default:
          return;
      }

      e.preventDefault();
      activate(target(tabs[next]), true);
    });

    /* Reaching a department by any means shows its pane, so Shift+Tab back into
       the rail never leaves the wrong pane on screen. */
    tabs.forEach(function (tab) {
      tab.addEventListener('focus', function () {
        window.clearTimeout(timer);
        if (target(tab) !== visiblePaneId()) activate(target(tab), false);
      });
    });

    function visiblePaneId() {
      for (var i = 0; i < panes.length; i++) {
        if (!panes[i].hidden) return panes[i].id;
      }
      return null;
    }

    /* --------------------------------------------------- reset on reopen */


    /* header.js opens the panel by clearing `hidden`. Watching for that is
       cheaper and less brittle than reaching into its toggle logic, and it
       guarantees the panel always opens on the first department rather than
       wherever the last visit left it. */
    var panel = root.closest('.mega--all');
    if (panel && 'MutationObserver' in window) {
      new MutationObserver(function () {
        if (!panel.hidden) activate(target(tabs[0]), false);
      }).observe(panel, { attributes: true, attributeFilter: ['hidden'] });
    }
  });

  /* =========================================================================
   * Top-bar department dropdowns — open on hover, no delay.
   *
   * Deliberately different from the All-departments rail above, which keeps its
   * 180ms intent delay. The rail is a vertical list you drag a pointer down, so
   * a delay stops it strobing; the bar is a horizontal row of separate targets
   * where any delay just reads as lag.
   *
   * Only the department items opt in via data-mega-hover. The All-departments
   * button stays click-to-open: it is the widest panel and it sits where the
   * pointer naturally crosses on its way to the bar, so hover-opening it would
   * reintroduce exactly the flashing this delay exists to prevent.
   *
   * Coarse pointers never reach any of this — header.js's click toggle stays
   * the whole interaction on touch.
   * ====================================================================== */

  var barToggles = Array.prototype.slice.call(document.querySelectorAll('[data-mega-hover]'));

  if (barToggles.length) {
    var everyToggle = Array.prototype.slice.call(document.querySelectorAll('[data-mega-toggle]'));

    var panelOf = function (t) {
      return document.getElementById(t.getAttribute('aria-controls'));
    };

    /* ------------------------------------------------------ keep on screen */

    /* A panel is anchored to its bar item, so a wide one under a late item can
       overrun the right edge. Which items overflow depends on the panel's
       column count and the viewport, not on its index, so measure and shift
       rather than guessing with :nth-last-child. Transform is used instead of
       `left` so the anchor stays declarative and one reset undoes it. */
    var place = function (panel) {
      panel.style.transform = '';
      if (panel.hidden) return;
      var host = document.querySelector('.header-depts__inner');
      if (!host) return;
      var h = host.getBoundingClientRect();
      var p = panel.getBoundingClientRect();
      var shift = 0;
      if (p.right > h.right) shift = h.right - p.right;
      /* If the panel is wider than the container, align it left rather than
         letting the clamp above push its start off the other side. */
      if (p.left + shift < h.left) shift = h.left - p.left;
      if (shift) panel.style.transform = 'translateX(' + Math.round(shift) + 'px)';
    };

    var deptPanels = barToggles.map(panelOf).filter(Boolean);

    /* Observing `hidden` covers every way a panel can open — hover here, click
       or Enter in header.js — without reaching into any of them. */
    if ('MutationObserver' in window) {
      deptPanels.forEach(function (pn) {
        new MutationObserver(function () {
          place(pn);
        }).observe(pn, { attributes: true, attributeFilter: ['hidden'] });
      });
    }

    window.addEventListener('resize', function () {
      deptPanels.forEach(place);
    });

    var closePanels = function (except) {
      everyToggle.forEach(function (t) {
        if (t === except) return;
        var pn = panelOf(t);
        t.setAttribute('aria-expanded', 'false');
        if (pn) pn.hidden = true;
      });
    };

    barToggles.forEach(function (toggle) {
      var item = toggle.closest('.header-depts__item') || toggle;

      toggle.addEventListener('mouseenter', function () {
        if (!fine.matches) return;
        var pn = panelOf(toggle);
        if (!pn) return;
        closePanels(toggle);
        toggle.setAttribute('aria-expanded', 'true');
        pn.hidden = false;
      });

      /* The panel is a child of the item, so moving the pointer down into the
         links never leaves the item and never triggers this. */
      item.addEventListener('mouseleave', function () {
        if (!fine.matches) return;
        var pn = panelOf(toggle);
        toggle.setAttribute('aria-expanded', 'false');
        if (pn) pn.hidden = true;
      });
    });

    /* With hover doing the opening, a pointer click on a department name should
       follow the link rather than toggle the panel shut. Capturing on document
       runs before header.js's own listener on the toggle, so stopping
       propagation here leaves the anchor's default navigation intact.

       detail === 0 means the click came from Enter or Space rather than a
       pointer, so keyboard users still get the disclosure behaviour and the
       dropdown does not become mouse-only. */
    document.addEventListener(
      'click',
      function (e) {
        if (!fine.matches) return;
        if (e.detail === 0) return;
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        var t = e.target && e.target.closest ? e.target.closest('[data-mega-hover]') : null;
        if (t) e.stopPropagation();
      },
      true,
    );
  }
})();
